import { useQuery } from '@apollo/client'
import { useMemo } from 'react'
import {
  isGlobalAudienceEnabled,
  isMultilangPocEnabled
} from '../../../constants/featureFlags'
import {
  AudienceData,
  GET_AUDIENCES
} from '../../../data/gql/questionnaire/queries/audiences'
import { useSingleMarketTranslationsEnabled } from '../../../hooks/localState/useNeedsTranslationVar'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import { useFieldwork } from '../../../hooks/useFieldwork'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import { useSurveyId } from '../../../hooks/useSurveyId'
import { getLanguageLabel } from '../../../utils/HelperFunctions'
import { useProjectType } from '../../Project/Project.hooks'
import {
  visibleInAllMarkets,
  visibleInAnyMarket
} from '../../Questionnaire/MarketVisibility/marketVisibility'
import { computeCompletion } from '../lib/completion'
import { extractCustomAudienceMembers } from '../lib/extractAudienceFields'
import { getTranslatableSurveyEntries } from '../lib/extractTranslatableFields'

// English is the source / master language the Translations editor authors
// against, regardless of the survey's country. A language at code === 'en'
// can't be "incomplete" by construction (it IS the master text), so it's
// always excluded from the completeness check below — otherwise a Belgium-
// style survey with English allocated would always look broken.
const MASTER_LANGUAGE = 'en'

export interface IncompleteLanguage {
  code: string
  name: string
  translated: number
  total: number
  pct: number
}

export interface TranslationsCompletenessResult {
  // Allocated, non-master languages that fall below 100% translated. Empty
  // when everything is in order — including when nothing is allocated, when
  // the survey isn't marked as needing translation, or while fieldwork is
  // still loading.
  incompleteLanguages: ReadonlyArray<IncompleteLanguage>
  hasIncompleteTranslations: boolean
}

const EMPTY: TranslationsCompletenessResult = {
  incompleteLanguages: [],
  hasIncompleteTranslations: false
}

// Checks every language allocated on the Audience page (except English, the
// master) against the live translation state and reports any whose
// completion percentage is below 100%. Wired into the launch-readiness
// chain on the Confirmation page so a researcher can't ship a survey whose
// respondent-side language picker would lie to participants.
//
// Behind useSingleMarketTranslationsEnabled() — the same gate as the Translations
// tab. A survey the researcher hasn't marked as needing translation short-circuits
// to EMPTY, so launch isn't blocked by a gate there's no tab to satisfy (and a
// questionnaire already authored in the respondent's language is never gated).
const useTranslationsCompletenessValidation =
  (): TranslationsCompletenessResult => {
    const projectId = useProjectId()
    const surveyId = useSurveyId()
    const waveId = useOptionalWaveId()
    const { fieldwork } = useFieldwork()
    const { draftQuestionnaire } = useGetDraftQuestionnaire()
    const translationsEnabled = useSingleMarketTranslationsEnabled()
    const { isMultiMarketV2 } = useProjectType()
    // Multi-market screeners are authored per market and can never be
    // translated; a single-market survey authors them in English, so keeps
    // gating on them.
    const globalAudienceOnly = isMultiMarketV2 && isGlobalAudienceEnabled()

    // Reuse the same audience query the Translations route reads from. Skipped
    // on the feature grant rather than on `translationsEnabled` so the audience
    // members are already in the cache the instant the checkbox is ticked —
    // otherwise the first render after ticking would count questionnaire fields
    // only and briefly report an incomplete language as complete. Free in
    // practice: SurveyErrorPanel's useGetDraftAudience already runs this exact
    // query, with the same variables, alongside every consumer of this hook.
    const { data: audienceData } = useQuery<AudienceData>(GET_AUDIENCES, {
      context: { clientName: 'questionnaire' },
      variables: { projectId, surveyId, waveId },
      skip: !isMultilangPocEnabled()
    })

    return useMemo<TranslationsCompletenessResult>(() => {
      if (!translationsEnabled) return EMPTY
      const allocations = fieldwork?.languageAllocations ?? []
      if (allocations.length === 0) return EMPTY

      // Includes questions nested in sections, so the launch gate can't pass a
      // survey that still has untranslated in-section questions (see
      // getTranslatableSurveyEntries).
      // This gate speaks for one market, so questions hidden here don't count.
      const isVisible = globalAudienceOnly
        ? visibleInAnyMarket([surveyId])
        : visibleInAllMarkets
      const entries = getTranslatableSurveyEntries(
        draftQuestionnaire?.entries ?? [],
        isVisible
      )
      const audienceGroups = extractCustomAudienceMembers(
        audienceData?.draftAudience.members ?? [],
        { globalOnly: globalAudienceOnly }
      )

      const incompleteLanguages: IncompleteLanguage[] = []
      for (const allocation of allocations) {
        if (allocation.language === MASTER_LANGUAGE) continue
        const stats = computeCompletion(
          entries,
          allocation.language,
          audienceGroups,
          isVisible
        )
        // Compare raw counts, not the rounded `pct`. computeCompletion()
        // rounds the percentage for the sidebar display, so a survey with
        // e.g. 199/200 fields translated reports pct=100 and would slip
        // through. The launch gate must reject anything with even one
        // missing field.
        if (stats.translated < stats.total) {
          incompleteLanguages.push({
            code: allocation.language,
            name: getLanguageLabel(allocation.language),
            translated: stats.translated,
            total: stats.total,
            pct: stats.pct
          })
        }
      }

      return {
        incompleteLanguages,
        hasIncompleteTranslations: incompleteLanguages.length > 0
      }
    }, [
      // Must stay in the deps: clearing "Needs translation" mid-session changes
      // nothing else the memo reads, so without it the stale non-EMPTY result
      // would keep the Launch button disabled until a reload.
      translationsEnabled,
      fieldwork?.languageAllocations,
      draftQuestionnaire?.entries,
      audienceData?.draftAudience.members,
      globalAudienceOnly,
      surveyId
    ])
  }

export default useTranslationsCompletenessValidation
